import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft, ArrowRight, Check, Tractor, Scissors, Sprout, TreePine, Wrench,
  Home as HomeIcon, RefreshCw, CalendarCheck, Sparkles, Loader2, PartyPopper,
} from "lucide-react";
import {
  SERVICES, SIZES, CONDITIONS, FREQUENCIES, calculateQuote,
  BUSINESS, LOGO_URL, API,
} from "@/lib/site";

const ICONS = { Tractor, Scissors, Sprout, TreePine, Wrench };
const STEPS = ["services", "size", "condition", "frequency"];

const slide = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.96 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.5 },
};

const slideIn = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.35 },
};

const progressTransition = { duration: 0.4 };

const EMPTY_BOOKING = {
  name: "", phone: "", address: "", preferred_date: "", preferred_time: "", special_requests: "",
};

// Bookings are not accepted during the 12:00–12:30pm break.
const lunchBlocked = (time) => !!time && time >= "12:00" && time < "12:30";

// 30-minute time slots from 7:00am to 4:00pm, excluding the 12:00pm lunch slot.
const to12h = (h, m) => {
  const period = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${period}`;
};
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 7; h <= 16; h++) {
    for (const m of [0, 30]) {
      if (h === 16 && m === 30) continue;
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      if (lunchBlocked(value)) continue;
      slots.push({ value, label: to12h(h, m) });
    }
  }
  return slots;
})();

export default function Quote() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ services: [], size: "", condition: "", frequency: "" });
  const [done, setDone] = useState(false);
  const [stage, setStage] = useState("result"); // result | booking | confirmed
  const [booking, setBooking] = useState(EMPTY_BOOKING);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const toggleService = (id) =>
    setAnswers((a) => ({
      ...a,
      services: a.services.includes(id) ? a.services.filter((s) => s !== id) : [...a.services, id],
    }));
  const setSingle = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  const canContinue = () => {
    if (step === 0) return answers.services.length > 0;
    if (step === 1) return !!answers.size;
    if (step === 2) return !!answers.condition;
    if (step === 3) return !!answers.frequency;
    return false;
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else { setDone(true); setStage("result"); }
  };
  const back = () => {
    if (step > 0) setStep((s) => s - 1);
    else navigate("/");
  };

  const restart = () => {
    setAnswers({ services: [], size: "", condition: "", frequency: "" });
    setBooking(EMPTY_BOOKING);
    setStep(0);
    setDone(false);
    setStage("result");
    setError("");
  };

  const progress = done ? 100 : (step / STEPS.length) * 100;
  const quote = done ? calculateQuote(answers) : null;
  const freqLabel = FREQUENCIES.find((f) => f.id === answers.frequency)?.label;
  const isRecurring = answers.frequency && answers.frequency !== "oneoff";
  const setField = (k) => (e) => setBooking((b) => ({ ...b, [k]: e.target.value }));

  const bookingValid =
    booking.name.trim() && booking.phone.trim() && booking.address.trim() &&
    booking.preferred_date && booking.preferred_time && !lunchBlocked(booking.preferred_time);

  const submitBooking = async () => {
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/bookings`, {
        ...booking,
        quote: {
          services: answers.services,
          size: answers.size,
          condition: answers.condition,
          frequency: answers.frequency,
          price_low: quote.priceLow,
          price_high: quote.priceHigh,
          low_hours: quote.lowHours,
          high_hours: quote.highHours,
        },
      });
      setStage("confirmed");
    } catch (e) {
      setError("Something went wrong sending your booking. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const showFooterNav = !done;

  const renderStage = () => {
    if (!done) {
      return (
        <motion.div key={step} {...slide}>
          <QuoteSteps step={step} answers={answers} toggleService={toggleService} setSingle={setSingle} />
        </motion.div>
      );
    }
    if (stage === "result") {
      return (
        <motion.div key="result" data-testid="quote-result" {...scaleIn}>
          <QuoteResult
            quote={quote}
            answers={answers}
            isRecurring={isRecurring}
            freqLabel={freqLabel}
            onBook={() => { setStage("booking"); window.scrollTo(0, 0); }}
            onRestart={restart}
          />
        </motion.div>
      );
    }
    if (stage === "booking") {
      return (
        <motion.div key="booking" data-testid="booking-form" {...slideIn}>
          <BookingForm
            booking={booking}
            setField={setField}
            quote={quote}
            error={error}
            submitting={submitting}
            bookingValid={bookingValid}
            onBack={() => setStage("result")}
            onSubmit={submitBooking}
          />
        </motion.div>
      );
    }
    return (
      <motion.div key="confirmed" data-testid="booking-confirmed" {...scaleIn}
        className="text-center py-8">
        <BookingConfirmed booking={booking} onHome={() => navigate("/")} onRestart={restart} />
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <header className="border-b border-line bg-cream/95 backdrop-blur sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <button data-testid="quote-home" onClick={() => navigate("/")} className="flex items-center gap-3">
            <img src={LOGO_URL} alt={BUSINESS.fullName} className="h-11 w-auto" />
          </button>
          <button data-testid="quote-exit" onClick={() => navigate("/")}
            className="text-sm font-semibold text-ink-soft hover:text-forest transition-colors">
            Exit
          </button>
        </div>
        <div className="h-1.5 bg-sand">
          <motion.div className="h-full bg-lawn" initial={false}
            animate={{ width: `${progress}%` }} transition={progressTransition} />
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {!done && (
          <p className="text-xs tracking-[0.2em] uppercase text-lawn font-bold mb-6" data-testid="quote-step-indicator">
            Step {step + 1} of {STEPS.length}
          </p>
        )}

        <AnimatePresence mode="wait">
          {renderStage()}
        </AnimatePresence>
      </main>

      {showFooterNav && (
        <footer className="border-t border-line bg-cream sticky bottom-0">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
            <button data-testid="quote-back" onClick={back}
              className="inline-flex items-center gap-2 text-sm font-bold text-ink-soft hover:text-forest transition-colors">
              <ArrowLeft className="w-4 h-4" /> {step === 0 ? "Home" : "Back"}
            </button>
            <button data-testid="quote-next" onClick={next} disabled={!canContinue()}
              className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-7 py-3.5 text-sm font-bold hover:bg-forest-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {step === STEPS.length - 1 ? "See My Price" : "Continue"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border-2 border-line bg-white px-4 py-3 text-ink placeholder:text-ink-soft/50 focus:border-forest focus:outline-none transition-colors";

const QuoteSteps = ({ step, answers, toggleService, setSingle }) => {
  if (step === 0) {
    return (
      <StepShell title="What would you like done?" sub="Select everything that applies — you can pick more than one.">
        <div className="grid sm:grid-cols-2 gap-3">
          {SERVICES.map((s) => {
            const Icon = ICONS[s.icon];
            const active = answers.services.includes(s.id);
            return (
              <button key={s.id} data-testid={`quote-service-${s.id}`} onClick={() => toggleService(s.id)}
                className={`relative flex items-start gap-4 text-left rounded-2xl border-2 p-5 transition-all ${active ? "border-forest bg-forest/5" : "border-line bg-white hover:border-forest/40"}`}>
                <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-forest text-cream" : "bg-sand text-forest"}`}>
                  <Icon className="w-6 h-6" strokeWidth={1.8} />
                </span>
                <span>
                  <span className="block font-heading font-bold text-ink">{s.label}</span>
                  <span className="block text-sm text-ink-soft mt-0.5">{s.desc}</span>
                </span>
                {active && <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-forest text-cream flex items-center justify-center"><Check className="w-4 h-4" /></span>}
              </button>
            );
          })}
        </div>
      </StepShell>
    );
  }

  if (step === 1) {
    return (
      <StepShell title="How big is the area?" sub="A rough idea is fine — it helps us estimate the time.">
        <div className="grid sm:grid-cols-2 gap-3">
          {SIZES.map((s) => {
            const active = answers.size === s.id;
            return (
              <button key={s.id} data-testid={`quote-size-${s.id}`} onClick={() => setSingle("size", s.id)}
                className={`flex items-center gap-4 text-left rounded-2xl border-2 p-5 transition-all ${active ? "border-forest bg-forest/5" : "border-line bg-white hover:border-forest/40"}`}>
                <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-forest text-cream" : "bg-sand text-forest"}`}>
                  <HomeIcon className="w-6 h-6" strokeWidth={1.8} />
                </span>
                <span>
                  <span className="block font-heading font-bold text-ink">{s.label}</span>
                  <span className="block text-sm text-ink-soft mt-0.5">{s.note}</span>
                </span>
              </button>
            );
          })}
        </div>
      </StepShell>
    );
  }

  if (step === 2) {
    return (
      <StepShell title="What's the current condition?" sub="Be honest — overgrown jobs simply take a little longer.">
        <div className="space-y-3">
          {CONDITIONS.map((c) => {
            const active = answers.condition === c.id;
            return (
              <button key={c.id} data-testid={`quote-condition-${c.id}`} onClick={() => setSingle("condition", c.id)}
                className={`w-full flex items-center justify-between text-left rounded-2xl border-2 p-5 transition-all ${active ? "border-forest bg-forest/5" : "border-line bg-white hover:border-forest/40"}`}>
                <span>
                  <span className="block font-heading font-bold text-ink">{c.label}</span>
                  <span className="block text-sm text-ink-soft mt-0.5">{c.note}</span>
                </span>
                <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${active ? "border-forest bg-forest text-cream" : "border-line"}`}>
                  {active && <Check className="w-4 h-4" />}
                </span>
              </button>
            );
          })}
        </div>
      </StepShell>
    );
  }

  return (
    <StepShell title="How often would you like us?" sub="Regular visits keep things easy and your yard always tidy.">
      <div className="grid sm:grid-cols-2 gap-3">
        {FREQUENCIES.map((f) => {
          const active = answers.frequency === f.id;
          return (
            <button key={f.id} data-testid={`quote-frequency-${f.id}`} onClick={() => setSingle("frequency", f.id)}
              className={`flex items-center gap-4 text-left rounded-2xl border-2 p-5 transition-all ${active ? "border-forest bg-forest/5" : "border-line bg-white hover:border-forest/40"}`}>
              <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-forest text-cream" : "bg-sand text-forest"}`}>
                {f.id === "oneoff" ? <CalendarCheck className="w-6 h-6" strokeWidth={1.8} /> : <RefreshCw className="w-6 h-6" strokeWidth={1.8} />}
              </span>
              <span>
                <span className="block font-heading font-bold text-ink">{f.label}</span>
                <span className="block text-sm text-ink-soft mt-0.5">{f.note}</span>
              </span>
            </button>
          );
        })}
      </div>
    </StepShell>
  );
};

const QuoteResult = ({ quote, answers, isRecurring, freqLabel, onBook, onRestart }) => (
  <>
    <div className="text-center">
      <span className="inline-flex items-center gap-2 rounded-full bg-lawn/15 text-forest px-4 py-2 text-xs font-bold uppercase tracking-wider">
        <Sparkles className="w-4 h-4" /> Your instant estimate
      </span>
    </div>

    <div className="mt-6 bg-forest text-cream rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-40" />
      <p className="relative text-cream/70 font-semibold">Estimated cost{isRecurring ? " per visit" : ""}</p>
      <p className="relative font-heading text-5xl sm:text-6xl font-extrabold mt-2" data-testid="quote-price-range">
        ${quote.priceLow} – ${quote.priceHigh}
      </p>
      <p className="relative text-cream/70 mt-3">
        Based on around {quote.lowHours}–{quote.highHours} hours of work
      </p>
    </div>

    <div className="mt-6 bg-white border border-line rounded-2xl p-6 sm:p-7">
      <h3 className="font-heading font-bold text-ink mb-4">Your job summary</h3>
      <dl className="space-y-3 text-sm">
        <SummaryRow label="Services" value={answers.services.map((id) => SERVICES.find((s) => s.id === id)?.label).join(", ")} />
        <SummaryRow label="Area size" value={SIZES.find((s) => s.id === answers.size)?.label} />
        <SummaryRow label="Condition" value={CONDITIONS.find((c) => c.id === answers.condition)?.label} />
        <SummaryRow label="Frequency" value={freqLabel} />
      </dl>
      <p className="mt-5 text-xs text-ink-soft leading-relaxed border-t border-line pt-4">
        This is a guide only based on the details you provided. Your final price is confirmed on-site.
        {isRecurring && " Regular visits are usually quicker once your yard is on a schedule."}
      </p>
    </div>

    <div className="mt-7 flex flex-col gap-3">
      <button data-testid="quote-book-now" onClick={onBook}
        className="group inline-flex items-center justify-center gap-2 rounded-full bg-lawn text-forest-dark px-8 py-4 text-base font-extrabold hover:bg-lawn-light transition-colors">
        I'm Happy — Book This In
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </button>
      <button data-testid="quote-restart" onClick={onRestart}
        className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-line text-ink-soft px-8 py-3.5 text-sm font-bold hover:border-forest hover:text-forest transition-colors">
        <RefreshCw className="w-4 h-4" /> Start a new quote
      </button>
    </div>
  </>
);

const BookingForm = ({ booking, setField, quote, error, submitting, bookingValid, onBack, onSubmit }) => (
  <>
    <button onClick={onBack} data-testid="booking-back"
      className="inline-flex items-center gap-2 text-sm font-bold text-ink-soft hover:text-forest mb-5">
      <ArrowLeft className="w-4 h-4" /> Back to my quote
    </button>
    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-ink tracking-tight">
      Let's lock in your booking
    </h1>
    <p className="mt-3 text-ink-soft">
      Pop in your details and Roddy will be in touch to confirm your visit. Estimate:{" "}
      <span className="font-bold text-forest">${quote.priceLow} – ${quote.priceHigh}</span>.
    </p>

    <div className="mt-8 space-y-5">
      <Field label="Full name" required>
        <input data-testid="booking-name" value={booking.name} onChange={setField("name")}
          placeholder="e.g. Jane Smith" className={inputCls} />
      </Field>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Phone number" required>
          <input data-testid="booking-phone" value={booking.phone} onChange={setField("phone")}
            inputMode="tel" placeholder="e.g. 0400 000 000" className={inputCls} />
        </Field>
        <Field label="Address" required>
          <input data-testid="booking-address" value={booking.address} onChange={setField("address")}
            placeholder="Street, suburb" className={inputCls} />
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Preferred date" required>
          <input data-testid="booking-date" type="date" value={booking.preferred_date} onChange={setField("preferred_date")}
            className={inputCls} />
        </Field>
        <Field label="Preferred time" required>
          <select data-testid="booking-time" value={booking.preferred_time} onChange={setField("preferred_time")}
            className={inputCls}>
            <option value="" disabled>Select a time</option>
            {TIME_SLOTS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <p className="mt-2 text-xs text-ink-soft">
            Please note: we don't take bookings between <span className="font-semibold text-ink">12:00pm and 12:30pm</span>.
          </p>
        </Field>
      </div>
      <Field label="Special requests or things we should know">
        <textarea data-testid="booking-notes" value={booking.special_requests} onChange={setField("special_requests")}
          rows={4} placeholder="e.g. access via side gate, please knock — I have PTSD, dog in the yard, pay attention to the front hedge…"
          className={`${inputCls} resize-none`} />
        <p className="mt-2 text-xs text-ink-soft">
          Anything that helps us give you the best, most considerate service — access notes, pets, sensitivities, priorities.
        </p>
      </Field>

      {error && (
        <p data-testid="booking-error" className="text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          {error}
        </p>
      )}

      <button data-testid="booking-submit" onClick={onSubmit} disabled={!bookingValid || submitting}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-forest text-cream px-8 py-4 text-base font-extrabold hover:bg-forest-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
        {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Sending…</>) : (<>Send My Booking Request <ArrowRight className="w-5 h-5" /></>)}
      </button>
      <p className="text-center text-xs text-ink-soft">
        Your details go straight to {BUSINESS.fullName}. No spam, ever.
      </p>
    </div>
  </>
);

const BookingConfirmed = ({ booking, onHome, onRestart }) => (
  <>
    <div className="w-20 h-20 rounded-full bg-lawn/20 text-forest flex items-center justify-center mx-auto">
      <PartyPopper className="w-10 h-10" />
    </div>
    <h1 className="mt-6 font-heading text-3xl sm:text-4xl font-extrabold text-ink tracking-tight">
      Booking request sent!
    </h1>
    <p className="mt-4 text-ink-soft max-w-md mx-auto">
      Thanks {booking.name.trim().split(" ")[0]}! Your details and quote have been sent to Roddy.
      You'll hear back soon to confirm your visit on{" "}
      <span className="font-semibold text-ink">
        {new Date(`${booking.preferred_date}T00:00:00`).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
      </span> at{" "}
      <span className="font-semibold text-ink">
        {TIME_SLOTS.find((s) => s.value === booking.preferred_time)?.label || booking.preferred_time}
      </span>.
    </p>
    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
      <button data-testid="confirmed-home" onClick={onHome}
        className="inline-flex items-center justify-center gap-2 rounded-full bg-forest text-cream px-8 py-4 text-base font-bold hover:bg-forest-dark transition-colors">
        Back to Home
      </button>
      <button data-testid="confirmed-restart" onClick={onRestart}
        className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-line text-ink-soft px-8 py-4 text-base font-bold hover:border-forest hover:text-forest transition-colors">
        New Quote
      </button>
    </div>
  </>
);

const StepShell = ({ title, sub, children }) => (
  <div>
    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-ink tracking-tight">{title}</h1>
    <p className="mt-3 text-ink-soft">{sub}</p>
    <div className="mt-8">{children}</div>
  </div>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-ink-soft">{label}</dt>
    <dd className="font-semibold text-ink text-right">{value}</dd>
  </div>
);

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-sm font-bold text-ink mb-2">
      {label} {required && <span className="text-lawn">*</span>}
    </span>
    {children}
  </label>
);
